import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import LaunchBanner from "../components/LaunchBanner";
import Header from "../components/Header";
import GiftModal from "../components/GiftModal";
import CopyrightFooter from "../components/CopyrightFooter";
import api from "../api";
import { track } from "../utils/analytics";
import { friendlyCopy } from "../utils/errorCopy";

import CheckMark from "../assets/check-mark.png";

import "../styles/PricingPage.css";
import "../styles/buttons.css";

const PricingPageCouples = () => {
  /* ───────── hooks / refs ───────── */
  const navigate = useNavigate();
  const location = useLocation();
  const plansRef = useRef(null);

  /* ───────── state ───────── */
  const [email, setEmail] = useState("");
  const [banner, setBanner] = useState(""); // success / error message
  const [loading, setLoading] = useState(false);
  const [lastErrorCode, setLastErrorCode] = useState(null);

  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [retrySecs, setRetrySecs] = useState(0);

  /* ───────── effects ───────── */

  /* GA4 page_view */
  useEffect(() => {
    track("page_view", {
      page_location: window.location.href,
      page_title: "Pricing – Couples",
    });
  }, []);

  /* body class tidy-up */
  useEffect(() => {
    document.body.classList.add("pricing-page-body");
    return () => document.body.classList.remove("pricing-page-body");
  }, []);

  /* hash deep-link scroll */
  useEffect(() => {
    if (location.hash === "#couples-plans" && plansRef.current) {
      plansRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  useEffect(() => {
    if (retrySecs <= 0) return;
    const id = setInterval(
      () => setRetrySecs((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [retrySecs]);

  /* ───────── helpers ───────── */

  /* trial email capture */
  const handleRequestTrial = async () => {
    track("request_trial_click", { account_type: "couple" });

    /* simple client-side validation */
    if (!email || !email.includes("@")) {
      setBanner("Please enter a valid email.");
      track("start_trial_error", {
        code: "INVALID_EMAIL_CLIENT",
        account_type: "couple",
      });
      return;
    }

    try {
      if (retrySecs > 0) return;
      setLoading(true);
      setBanner(""); // clear old message
      const { data } = await api.post("/couple_trial/start", { email });

      if (data.status === "success") {
        track("start_trial_success", { account_type: "couple" });
        window.gtag?.("set", "user_properties", { has_trial: true });
        setLastErrorCode(null);
        setBanner("✅ Check your inbox for the magic link!");
        setEmail("");
      } else {
        const code = data?.code || "UNKNOWN";
        setLastErrorCode(code);
        const msg = friendlyCopy(code, data?.user_message);
        if (data?.retry_after) setRetrySecs(Number(data.retry_after) || 0);
        setBanner(`❌ ${msg}`);
        track("start_trial_error", { code, account_type: "couple" });
      }
    } catch (err) {
      const resp = err?.response?.data;
      const code = resp?.code || "NETWORK_OR_UNKNOWN";
      setLastErrorCode(code);
      const msg = friendlyCopy(code, resp?.user_message);
      const retryAfter = resp?.retry_after;
      if (retryAfter) setRetrySecs(Number(retryAfter) || 0);
      setBanner(`❌ ${msg}`);
      track("start_trial_error", {
        code,
        http_status: err?.response?.status ?? null,
        account_type: "couple",
      });
    } finally {
      setLoading(false);
    }
  };

  /* open the Gift modal */
  const openGiftModal = (planId) => {
    track("gift_modal_open", { plan_id: planId });
    setSelectedPlan(planId);
    setGiftModalOpen(true);
  };

  /* close (wrapper so we can fire event) */
  const handleCloseGiftModal = (closedVia = "btn") => {
    track("gift_modal_close", { plan_id: selectedPlan, closed_via: closedVia });
    setGiftModalOpen(false);
    setSelectedPlan(null);
  };

  /* navigate to signup with plan selected */
  const goToSignup = (plan) => {
    track("select_plan_click", { plan_id: plan, billing: "self" });
    window.gtag?.("set", "user_properties", { chosen_plan: plan });
    navigate("/signup", { state: { accountType: "couple", plan } });
  };

  /* ───────── render ───────── */
  return (
    <div className="pricing-page">
      <LaunchBanner goToSignup={goToSignup} />
      <Header />

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <div className="content-above-cards">
        <header className="pricing-header">
          <h1 className="pricing-tagline">Plans &amp; Pricing</h1>
          <p className="pricing-subheading">
            Get Started for Free and Choose the Plan that Fits Your Needs.
          </p>
          <p className="pricing-description">
            Pick the Perfect Plan for Your Event. Upgrade or Adjust as Your
            Needs Evolve.
          </p>

          {/* trial email capture */}
          <div className="email-trial">
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRequestTrial}
              disabled={loading || retrySecs > 0}
            >
              {loading
                ? "Requesting..."
                : retrySecs > 0
                ? `Try again in ${retrySecs}s`
                : "Request a trial"}
            </button>
          </div>

          {/* banner / spinner */}
          <div className="banner-holder">
            {loading && <span className="spinner" />}
            {!loading && banner && (
              <p className="banner-message">
                {banner}
                {/* Optional CTA for duplicate: show Sign in */}
                {lastErrorCode === "TRIAL_ALREADY_USED" && (
                  <>
                    {" "}
                    <a
                      href="/signin"
                      onClick={() =>
                        track("signin_cta_click", { source: "trial_duplicate" })
                      }
                    >
                      Sign in
                    </a>
                    .
                  </>
                )}
              </p>
            )}
          </div>

          <p className="no-credit-info">
            Try BestDay2Marry free, no credit card required. By entering your
            email, you agree to receive marketing emails from BestDay2Marry.
          </p>
        </header>
      </div>

      {/* ─── CARDS ────────────────────────────────────────────── */}
      <div
        ref={plansRef}
        id="couples-plans"
        className="pricing-cards-container"
      >
        <header className="plans-header">
          <h2>Couples&#39; Plans</h2>
        </header>

        <div className="pricing-cards">
          {/* ---------- Essentials ---------- */}
          <div className="pricing-card">
            <h3 className="plan-title">Essentials</h3>
            <p className="price">$10</p>

            <div className="access-details">
              <p className="access-period">1 Month Access:</p>
              <ul className="access-list">
                <li>1 Location</li>
                <li>1 Date</li>
              </ul>
            </div>

            <div className="card-divider" />

            <ul className="features-list">
              {[
                "Temperature",
                "Humidity",
                "Wind",
                "Clouds",
                "Precipitation",
                "Sunrise/Sunset",
              ].map((feat) => (
                <li key={feat}>
                  <img src={CheckMark} alt="✓" /> {feat}
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => goToSignup("essentials")}
            >
              Get started
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => openGiftModal("couples_essentials")}
            >
              Gift this plan
            </button>
          </div>

          {/* ---------- Standard ---------- */}
          <div className="pricing-card">
            <h3 className="plan-title">Standard</h3>
            <p className="price">$50</p>

            <div className="access-details">
              <p className="access-period">3 Month Access:</p>
              <ul className="access-list">
                <li>1 Location</li>
                <li>Any Date</li>
                <li>Monthly Updates</li>
              </ul>
            </div>

            <div className="card-divider" />

            <ul className="features-list">
              {[
                "Temperature",
                "Humidity",
                "Wind",
                "Clouds",
                "Precipitation",
                "Sunrise/Sunset",
              ].map((feat) => (
                <li key={feat}>
                  <img src={CheckMark} alt="✓" /> {feat}
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => goToSignup("standard")}
            >
              Get started
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => openGiftModal("couples_standard")}
            >
              Gift this plan
            </button>
          </div>

          {/* ---------- Premium ---------- */}
          <div className="pricing-card">
            <h3 className="plan-title">Premium</h3>
            <p className="price">$100</p>

            <div className="access-details">
              <p className="access-period">6 Month Access:</p>
              <ul className="access-list">
                <li>Any Location</li>
                <li>Any Date</li>
                <li>Monthly Updates</li>
              </ul>
            </div>

            <div className="card-divider" />

            <ul className="features-list">
              {[
                "Temperature",
                "Humidity",
                "Wind",
                "Clouds",
                "Precipitation",
                "Sunrise/Sunset",
              ].map((feat) => (
                <li key={feat}>
                  <img src={CheckMark} alt="✓" /> {feat}
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => goToSignup("premium")}
            >
              Get started
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => openGiftModal("couples_premium")}
            >
              Gift this plan
            </button>
          </div>
        </div>
      </div>

      {/* ─── Gift modal & footer ──────────────────────────────── */}
      <GiftModal
        open={giftModalOpen}
        planId={selectedPlan}
        onClose={() => handleCloseGiftModal("btn")}
      />
      <CopyrightFooter />
    </div>
  );
};

export default PricingPageCouples;
